import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import Loader from "../../pages/Loader";
import {
  FiFileText,
  FiCamera,
  FiUpload,
  FiSave,
  FiArrowRight,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiTrash2,
  FiImage
} from "react-icons/fi";
import {
  FaChartLine,
  FaRegFileAlt,
  FaUpload as FaUploadIcon,
  FaRegImage
} from "react-icons/fa";

const AddPlan = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  const watchedTitle = watch("title");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setPreview(URL.createObjectURL(file));
      // Set the file in the input
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      document.getElementById('plan-image').files = dataTransfer.files;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const formData = new FormData();
      const photoFile = data.photo[0];

      formData.append('file', photoFile);
      formData.append('upload_preset', 'prayagproperties');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/djmyigser/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      const cloudinaryData = await cloudinaryResponse.json();
      if (!cloudinaryData.secure_url) {
        throw new Error('Failed to upload image to Cloudinary');
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/create_plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: data.title,
          photo: cloudinaryData.secure_url,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        Swal.fire({
          title: "Success!",
          text: "Plan created successfully.",
          icon: "success",
          confirmButtonColor: "#6366f1",
          timer: 2000,
          timerProgressBar: true,
          background: "#ffffff"
        });
        reset();
        setPreview(null);
      } else {
        Swal.fire({
          title: "Error",
          text: result.message || "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#ef4444"
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#ef4444"
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaChartLine className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-xl">Add New Plan</h2>
                <p className="text-indigo-100 text-sm mt-0.5">Create a new business plan</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Title Field */}
            <div>
              <label className={labelClasses}>
                Plan Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiFileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  {...register("title", { required: "Title is required" })}
                  className={`${inputClasses} pl-10 ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="Enter plan title"
                />
              </div>
              {errors.title && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Image Upload Field */}
            <div>
              <label className={labelClasses}>
                Plan Image <span className="text-red-500">*</span>
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                  isDragging 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : preview 
                      ? 'border-emerald-300 bg-emerald-50/30'
                      : errors.photo 
                        ? 'border-red-300 bg-red-50/30' 
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                }`}
                onClick={() => document.getElementById('plan-image').click()}
              >
                <input
                  id="plan-image"
                  type="file"
                  accept="image/*"
                  {...register("photo", { required: "Photo is required" })}
                  onChange={handleImageChange}
                  className="hidden"
                />
                
                {preview ? (
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-40 h-40 object-cover rounded-xl shadow-lg border-4 border-white"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview(null);
                          document.getElementById('plan-image').value = '';
                        }}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-sm text-emerald-600 mt-3 flex items-center gap-1">
                      <FiCheckCircle className="w-4 h-4" />
                      Image uploaded successfully
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-3">
                      <FiCamera className="w-6 h-6 text-indigo-500" />
                    </div>
                    <p className="text-gray-600 font-medium">Click or drag to upload</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                  </div>
                )}
              </div>
              {errors.photo && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.photo.message}
                </p>
              )}
            </div>

            {/* Preview Section */}
            {watchedTitle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100"
              >
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4 text-emerald-600" />
                  Plan Preview
                </h4>
                <div className="flex items-center gap-4">
                  {preview ? (
                    <img src={preview} alt="Preview" className="w-16 h-16 rounded-lg object-cover border-2 border-indigo-300" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <FaRegImage className="w-6 h-6 text-indigo-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-800">{watchedTitle}</p>
                    <p className="text-xs text-gray-500">Business Plan</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating Plan...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Create Plan
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            {/* Security Note */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                Plan information is securely stored
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddPlan;